import DogYoung from 'shared/assets/doodles/dog-young.webp';
import DogAdult from 'shared/assets/doodles/dog-adult.webp';
import DogSenior from 'shared/assets/doodles/dog-senior.webp';
import { useRef, useState } from 'react';
import { Pressable, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import { Props } from 'shared/@types';
import type { Breed } from 'shared/@generated/models';
import { Text } from 'shared/components/text';
import { H1 } from 'shared/components/title';
import { Image } from 'shared/components/image';
import { MarkdownView } from 'shared/components/markdown-view';
import { BreedInput } from 'shared/components/form/breed-input';
import { DropdownSelect } from 'shared/components/form/dropdown-select';
import useApi from 'shared/library/use-api';
import { apiRoute } from 'shared/library/api-route';
import { notify } from 'shared/library/notify';
import { useRoute } from 'shared/library/routes/route';
import { translateArray } from 'shared/library/i18n/get-translation-list';
import {
  DOG_YEARS_AGES,
  DogYearsStage,
  dogYearsBirthdays,
  dogYearsProfile,
  dogYearsStage,
  humanAge,
} from 'shared/library/dog-years';

type DogYearsScreenProps = Props;

const stageImages: Record<DogYearsStage, string> = {
  young: DogYoung,
  adult: DogAdult,
  senior: DogSenior,
};

const stageColors: Record<DogYearsStage, string> = {
  young: 'bg-brand-yellow',
  adult: 'bg-brand-sky',
  senior: 'bg-brand-lilac',
};

const stages: DogYearsStage[] = ['young', 'adult', 'senior'];

const DogYearsScreen = (_props: DogYearsScreenProps) => {
  const { t, i18n } = useTranslation('screens');
  const route = useRoute();
  const { request } = useApi({ notifyOnServerErrors: false });
  const [breed, setBreed] = useState<Breed | null>(null);
  const [breedId, setBreedId] = useState<number | null>(null);
  const [age, setAge] = useState(5);
  const requestId = useRef(0);
  const profile = dogYearsProfile(breed);
  const human = humanAge(profile, age);
  const birthdays = dogYearsBirthdays(profile);
  // Puppy bars at 3, 6 and 9 months, plus the chosen month when it is another one.
  const months = [...new Set([3, 6, 9, ...(age < 1 ? [Math.round(age * 12)] : [])])]
    .sort((a, b) => a - b)
    .map((month) => month / 12);
  const bars = [...months, ...birthdays];
  const selected = age < 1 ? age : Math.min(birthdays.length, age);
  const yMax = Math.ceil((humanAge(profile, birthdays.length) + 10) / 20) * 20;
  const ticks = Array.from({ length: yMax / 20 + 1 }, (_, index) => index * 20);
  const numberFormatter = new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 });

  const stageOfAge = age < 1 ? 'young' : dogYearsStage(profile, Math.max(1, Math.round(age)));
  const ageLabel = (value: number) =>
    value < 1
      ? t('dog_years.age_months', { count: Math.round(value * 12) })
      : t('dog_years.age_years', { count: value, age: numberFormatter.format(value) });

  function selectBreed(next: Breed) {
    const id = ++requestId.current;

    setBreedId(next.id);

    request<{ breed: Breed }>(apiRoute('breeds.show', { breed: next.slug.en }))
      .then(({ breed: details }) => {
        if (id === requestId.current) setBreed(details);
      })
      .catch((error) => {
        if (id !== requestId.current) return;

        setBreedId(breed?.id ?? null);
        notify.error(error, 'dog-years.tsx - selectBreed');
      });
  }

  function clearBreed() {
    requestId.current++;
    setBreedId(null);
    setBreed(null);
  }

  const sections = translateArray<{ title: string; text: string }>(t, 'dog_years.article.sections');
  const sources = translateArray<{ title: string; url: string }>(t, 'dog_years.article.sources');
  const articleMarkdown = [
    `## ${t('dog_years.article.title')}`,
    t('dog_years.article.intro'),
    ...sections.map((section) => `### ${section.title}\n\n${section.text}`),
    `### ${t('dog_years.more')}`,
    [
      `- [${t('pet_names.pages.dog.link_title')}](${route('pet-names.dog')}): ${t('pet_names.pages.dog.link_description')}`,
      `- [${t('pet_costs.pages.dog.link_title')}](${route('pet-costs.dog')}): ${t('pet_costs.pages.dog.link_description')}`,
    ].join('\n'),
    `### ${t('dog_years.sources')}`,
    sources.map((source) => `- [${source.title}](${source.url})`).join('\n'),
  ].join('\n\n');

  const breedField = (
    <>
      <View className={'gap-1.5'}>
        <Text className={'text-brand-brown-dark text-sm font-bold'}>
          {t('dog_years.breed_label')}
        </Text>
        <BreedInput
          animal={'dog'}
          allowQuery={false}
          historyEnabled={false}
          value={breedId ? [breedId] : []}
          onSelectBreed={selectBreed}
          onChange={(values) => {
            if (values.length === 0) clearBreed();
          }}
        />
      </View>
    </>
  );
  const chart = (
    <>
      <Text className={'text-brand-brown-dark text-lg font-black md:text-2xl'}>
        {t('dog_years.chart_title')}
      </Text>
      <Text className={'text-brand-brown-dark/60 mt-1 text-sm md:text-base'}>
        {t('dog_years.chart_subtitle')}
      </Text>

      <View
        role={'img'}
        aria-label={t('dog_years.chart_label', { years: birthdays.length })}
        className={'mt-6 h-72 flex-row md:h-96'}
      >
        <View className={'relative w-8 md:w-10'}>
          {ticks.map((tick) => (
            <Text
              key={tick}
              className={'text-brand-brown-dark/50 absolute right-2 -mb-2 text-xs tabular-nums'}
              style={{ bottom: `${(tick / yMax) * 100}%` }}
            >
              {tick}
            </Text>
          ))}
        </View>

        <View className={'relative flex-1'}>
          {ticks.map((tick) => (
            <View
              key={tick}
              className={clsx(
                'absolute inset-x-0 h-px',
                tick ? 'bg-brand-brown-dark/10' : 'bg-brand-brown-dark/30',
              )}
              style={{ bottom: `${(tick / yMax) * 100}%` }}
            />
          ))}

          <View className={'flex-1 flex-row items-end gap-1 md:gap-2'}>
            {bars.map((bar) => {
              const value = humanAge(profile, bar);
              const stage = bar < 1 ? 'young' : dogYearsStage(profile, bar);
              const active = bar === selected;

              return (
                <Pressable
                  key={bar}
                  role={'button'}
                  aria-label={t('dog_years.bar_label', {
                    age: ageLabel(bar),
                    human: value,
                  })}
                  aria-pressed={active}
                  onPress={() => setAge(bar)}
                  testID={
                    bar < 1 ? `dog-years-bar-${Math.round(bar * 12)}m` : `dog-years-bar-${bar}`
                  }
                  className={'h-full flex-1 justify-end'}
                >
                  {active && (
                    <Image
                      src={stageImages[stage]}
                      alt={''}
                      imageClassName={'object-contain'}
                      containerClassName={'mb-1 h-10 w-14 self-center md:h-14 md:w-20'}
                    />
                  )}
                  <View
                    className={clsx(
                      'items-center rounded-t-lg pt-1.5 md:rounded-t-xl md:pt-2',
                      active
                        ? 'border-brand-brown-dark border-2 bg-orange-500'
                        : clsx(stageColors[stage], 'hover:opacity-80'),
                    )}
                    style={{ height: `${(value / yMax) * 100}%`, minHeight: 28 }}
                  >
                    <Text
                      className={clsx(
                        'text-xs font-black tabular-nums md:text-sm',
                        active ? 'text-white' : 'text-brand-brown-dark',
                      )}
                    >
                      {value}
                    </Text>
                  </View>
                </Pressable>
              );
            })}
          </View>
        </View>
      </View>

      <View className={'ml-8 mt-2 flex-row gap-1 md:ml-10 md:gap-2'}>
        {bars.map((bar) => (
          <Text
            key={bar}
            className={clsx(
              'text-brand-brown-dark flex-1 text-center text-xs tabular-nums',
              bar === selected ? 'font-black' : 'opacity-50',
            )}
          >
            {bar < 1 ? Math.round(bar * 12) : bar}
          </Text>
        ))}
      </View>
      <View className={'ml-8 mt-1 flex-row gap-1 md:ml-10 md:gap-2'}>
        <Text
          className={
            'text-brand-brown-dark/50 border-brand-brown-dark/10 border-t pt-1 text-center text-xs'
          }
          style={{ flex: months.length }}
        >
          {t('dog_years.x_axis_months')}
        </Text>
        <Text
          className={
            'text-brand-brown-dark/50 border-brand-brown-dark/10 border-t pt-1 text-center text-xs'
          }
          style={{ flex: birthdays.length }}
        >
          {t('dog_years.x_axis_years')}
        </Text>
      </View>

      <View className={'mt-4 flex-row flex-wrap gap-x-4 gap-y-2'}>
        {stages.map((stage) => (
          <View key={stage} className={'flex-row items-center gap-1.5'}>
            <View className={clsx('size-3 rounded-full', stageColors[stage])} />
            <Text className={'text-brand-brown-dark/70 text-sm'}>
              {t(`dog_years.stages.${stage}`)}
            </Text>
          </View>
        ))}
        <View className={'flex-row items-center gap-1.5'}>
          <View className={'size-3 rounded-full bg-orange-500'} />
          <Text className={'text-brand-brown-dark/70 text-sm'}>{t('dog_years.selected')}</Text>
        </View>
      </View>
    </>
  );
  const ageCompact = (
    <View testID={'dog-years-age'} className={'gap-1.5'}>
      <Text className={'text-brand-brown-dark text-sm font-bold'}>{t('dog_years.age_label')}</Text>
      <DropdownSelect<number>
        title={t('dog_years.age_label')}
        options={DOG_YEARS_AGES.map((value) => ({ value, label: ageLabel(value) }))}
        value={age}
        onChange={(value) => value !== null && setAge(value)}
        triggerClassName={'w-full justify-between'}
      />
    </View>
  );
  const result = (
    <View
      className={
        'border-brand-brown-dark/15 -mx-4 flex-1 items-center justify-center border-t px-4 pt-6 md:-mx-6 md:px-6 md:pt-8'
      }
    >
      <Image
        src={stageImages[stageOfAge]}
        alt={''}
        imageClassName={'object-contain'}
        containerClassName={'h-20 w-28 md:h-24 md:w-32'}
      />
      <View className={'mt-4 w-full flex-row items-end'}>
        <View className={'flex-1 items-center gap-1'}>
          <Text className={'text-brand-brown-dark/60 text-sm font-bold'}>
            {t('dog_years.age_label')}
          </Text>
          <Text className={'text-brand-brown-dark text-4xl font-black tabular-nums'}>
            {ageLabel(age)}
          </Text>
        </View>
        <Text className={'text-brand-brown-dark/30 text-4xl font-black'}>=</Text>
        <View className={'flex-1 items-center gap-1'}>
          <Text className={'text-brand-brown-dark/60 text-sm font-bold'}>
            {t('dog_years.result_label')}
          </Text>
          <Text
            testID={'dog-years-result'}
            className={'text-brand-brown-dark text-4xl font-black tabular-nums'}
          >
            {t('dog_years.age_years', { count: human, age: human })}
          </Text>
        </View>
      </View>
      <View className={clsx('mt-5 rounded-full px-3 py-1', stageColors[stageOfAge])}>
        <Text className={'text-brand-brown-dark text-sm font-bold'}>
          {t(`dog_years.stages.${stageOfAge}`)}
        </Text>
      </View>
    </View>
  );

  return (
    <View className={'px-3 py-6 md:px-6 md:py-12'}>
      <View className={'mx-auto w-full max-w-screen-xl'}>
        <View className={'md:items-center'}>
          <H1 title={t('dog_years.heading')} className={'md:text-center'} marginBottom={false} />
          <Text
            className={
              'text-brand-brown-dark mt-1.5 max-w-prose text-pretty md:mt-3 md:text-center md:text-lg'
            }
          >
            {t('dog_years.intro')}
          </Text>
        </View>

        <View className={'mt-6 gap-4 md:mt-10 lg:flex-row lg:items-stretch lg:gap-5'}>
          <View
            className={
              'border-brand-brown-dark/15 gap-6 rounded border bg-white p-4 md:p-6 lg:w-96'
            }
          >
            {breedField}
            {ageCompact}
            {result}
          </View>
          <View className={'border-brand-brown-dark/15 flex-1 rounded border bg-white p-4 md:p-6'}>
            {chart}
          </View>
        </View>

        <MarkdownView className={'mt-12 md:mt-16'} markdown={articleMarkdown} />
      </View>
    </View>
  );
};

export { DogYearsScreen };
export type { DogYearsScreenProps };
